/**
 * PRICING REVIEW — derivation layer
 * =================================
 * Everything the reviewer needs computed FOR them, so the screen can be read
 * rather than worked out. No JSX here on purpose: the arithmetic is the part
 * that must be testable and obviously correct.
 *
 * The shapes this consumes come straight from /admin/get_vendor_product:
 *   planType[]  { id, name, template: recurring|credit|one_time, ... }
 *   intervals[] { typeId (INDEX into planType), interval, n_interval, ... }
 *   plans[]     { id, name, typeId (index), interval (INDEX into intervals),
 *                 currency, price_type, unit_price_type, unit_ids[], features[] }
 *   unit_ids[]  { id, StartRange, EndRange, Steps, Marketplace_price,
 *                 Vendor_price, Industry_price, Min_price, Max_price, DefaultValue }
 *
 * Note the two index-typed fields. `plan.interval` is a position in the FLAT
 * intervals array, not an interval id, and `plan.typeId` is a position in
 * planType. Getting that wrong silently renders an empty grid, which is part
 * of why the old screen only ever showed one interval at a time.
 */

import { isDefaultRow } from "../../../utils/unitPrice";

/** The house vendor is Easexpense itself; it has no carve-out to check. */
export const ADMIN_VENDOR_ID = -1;

/* ── numbers ─────────────────────────────────────────────────────────── */

export const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const SYMBOL = { INR: "₹", USD: "$", EUR: "€", GBP: "£" };
export const symbolFor = (currency) => SYMBOL[currency] || `${currency || ""} `;

/** Indian grouping for INR, plain grouping otherwise. Never rounds silently. */
export const money = (v, currency = "INR") => {
  const n = num(v);
  if (n === null) return "—";
  const locale = currency === "INR" ? "en-IN" : "en-US";
  const decimals = Math.abs(n) > 0 && Math.abs(n) < 1 ? 4 : 2;
  // Sign OUTSIDE the symbol: "-₹500", never "₹-500". A negative carve-out is
  // the single most important number on this screen and it must read as one.
  const abs = Math.abs(n);
  return (
    (n < 0 ? "-" : "") +
    symbolFor(currency) +
    abs.toLocaleString(locale, {
      minimumFractionDigits: abs % 1 === 0 ? 0 : decimals,
      maximumFractionDigits: decimals,
    })
  );
};

export const pct = (v, digits = 1) =>
  v === null || v === undefined || !Number.isFinite(v)
    ? "—"
    : `${v > 0 ? "+" : ""}${v.toFixed(digits)}%`;

/* ── billing periods ─────────────────────────────────────────────────── */

/** Months per one unit of each interval, for normalising to a monthly rate. */
const MONTHS = { day: 1 / 30.4375, week: 7 / 30.4375, month: 1, quarter: 3, year: 12 };

export const periodLabel = (interval) => {
  if (!interval) return "—";
  const n = Number(interval.n_interval) || 1;
  const unit = String(interval.interval || "").toLowerCase();
  if (unit === "lifetime") return "One-time";
  if (n === 1) {
    if (unit === "month") return "Monthly";
    if (unit === "year") return "Yearly";
    if (unit === "quarter") return "Quarterly";
    if (unit === "week") return "Weekly";
    if (unit === "day") return "Daily";
  }
  return `Every ${n} ${unit}${n === 1 ? "" : "s"}`;
};

/** How many months one billing cycle covers. null = not time-based. */
export const monthsIn = (interval) => {
  const unit = String(interval?.interval || "").toLowerCase();
  const per = MONTHS[unit];
  if (!per) return null;
  return (Number(interval?.n_interval) || 1) * per;
};

/* ── unit rows ───────────────────────────────────────────────────────── */

/**
 * The row a buyer actually pays at qty 1 — the flagged default, else the first.
 * DefaultValue arrives as the STRING "true"/"false" from the DB, which is why
 * this goes through isDefaultRow rather than a truthiness check.
 */
export const headlineUnit = (plan) => {
  const units = plan?.unit_ids || [];
  return units.find(isDefaultRow) || units[0] || null;
};

export const sortedTiers = (plan) =>
  [...(plan?.unit_ids || [])].sort(
    (a, b) => (num(a.StartRange) ?? 0) - (num(b.StartRange) ?? 0),
  );

/**
 * The carve-out. Easexpense is merchant of record and earns the SPREAD between
 * what the buyer pays and what the vendor is owed — it is not a mark-up added
 * on top, and vendor_price is derived upstream rather than typed. Margin % is
 * therefore taken on the sell price, the way a retailer states margin.
 */
export const marginOf = (unit) => {
  const marketplace = num(unit?.Marketplace_price);
  const vendor = num(unit?.Vendor_price);
  if (marketplace === null || vendor === null) {
    return { marketplace, vendor, spread: null, spreadPct: null };
  }
  const spread = marketplace - vendor;
  return {
    marketplace,
    vendor,
    spread,
    spreadPct: marketplace === 0 ? null : (spread / marketplace) * 100,
  };
};

/* ── change_log plumbing ─────────────────────────────────────────────── */

/**
 * Cap findings carry the LIVE unit id plus the plan name. For a reseller the
 * submitted unit ids ARE live ids and the join is exact; for an owner they are
 * temp ids from a separate id-space, so we fall back to matching on plan name.
 * The fallback is deliberately coarse — better an over-attributed warning than
 * a silently dropped one.
 */
export const indexCapFindings = (changeJson) => {
  const findings = changeJson?.price_cap?.findings;
  const byUnit = new Map();
  const byPlanName = new Map();
  if (!Array.isArray(findings)) return { byUnit, byPlanName, resolvable: new Set(), all: [] };
  findings.forEach((f) => {
    if (f?.unit_id !== undefined && f?.unit_id !== null) byUnit.set(String(f.unit_id), f);
    const name = String(f?.plan_name || "").trim().toLowerCase();
    if (name) {
      if (!byPlanName.has(name)) byPlanName.set(name, []);
      byPlanName.get(name).push(f);
    }
  });
  return { byUnit, byPlanName, resolvable: new Set(), all: findings };
};

const findCap = (capIndex, plan, unit) => {
  if (unit?.id !== undefined && unit?.id !== null) {
    const hit = capIndex.byUnit.get(String(unit.id));
    if (hit) return hit;
  }
  // The name fallback exists only for findings whose unit_id belongs to a
  // different id-space than the one we were served (owner submissions stage
  // temp ids). If the finding's unit IS present in this payload it has already
  // been attached above, and re-attaching it by name would double-count it
  // across every same-named plan — which is how "Basic monthly" and "Basic
  // yearly" both ended up flagged from a single monthly finding.
  const list = (capIndex.byPlanName.get(String(plan?.name || "").trim().toLowerCase()) || [])
    .filter((f) => !capIndex.resolvable.has(String(f.unit_id)));
  return list.length === 1 ? list[0] : null;
};

/* ── validation ──────────────────────────────────────────────────────── */

/**
 * Severity is a verdict, not decoration:
 *   block  — publishing this charges someone the wrong amount. Red.
 *   review — legitimate but a human must agree to it. Amber.
 *   info   — worth knowing, needs no decision. Blue.
 * The cap breach is deliberately `review`, never `block`: an underlying licence
 * cost really can jump, so it routes to a human rather than being refused.
 */
const check = (severity, code, text) => ({ severity, code, text });

function checksForPrice({ plan, unit, margin, capFinding, isHouseVendor, currency }) {
  const out = [];
  const label = `${plan.name}`;

  if (plan.price_type === "contact") {
    out.push(check("info", "contact", `${label} is "contact us" — no price to verify.`));
    return out;
  }

  if (!unit) {
    out.push(check("block", "no_tier", `${label} has no price row at all.`));
    return out;
  }

  if (margin.marketplace === null || margin.marketplace === 0) {
    out.push(
      check(
        "block",
        "zero_price",
        `${label} would be published at ${money(0, currency)} — buyers would be charged nothing.`,
      ),
    );
  }

  if (!isHouseVendor && margin.spread !== null) {
    if (margin.spread < 0) {
      out.push(
        check(
          "block",
          "negative_margin",
          `${label} sells below cost: buyer pays ${money(margin.marketplace, currency)}, ` +
            `vendor is owed ${money(margin.vendor, currency)} — Easexpense loses ` +
            `${money(Math.abs(margin.spread), currency)} per unit.`,
        ),
      );
    } else if (margin.spread === 0 && margin.marketplace > 0) {
      out.push(
        check("review", "zero_margin", `${label} carries no carve-out — we earn nothing on it.`),
      );
    }
  }

  if (!isHouseVendor && margin.marketplace > 0 && margin.vendor === null) {
    out.push(
      check(
        "review",
        "no_vendor_price",
        `${label} has no vendor price, so the carve-out cannot be verified.`,
      ),
    );
  }

  if (capFinding) {
    const change = typeof capFinding.change === "number" ? capFinding.change * 100 : null;
    const limit = typeof capFinding.limit === "number" ? capFinding.limit * 100 : 25;
    out.push(
      check(
        "review",
        "over_cap",
        change === null
          ? `${label}: ${capFinding.summary || "price move exceeds the cap"}.`
          : `${label} moves ${pct(change)} against ${money(capFinding.baseline, currency)} ` +
            `— cap is ±${limit.toFixed(0)}%/year, ` +
            `${Math.abs(Math.abs(change) - limit).toFixed(1)}pp over.`,
      ),
    );
  }

  const tiers = sortedTiers(plan);
  if (tiers.length > 1) {
    out.push(...tierChecks(plan, tiers, currency));
  }

  if (!String(plan.description || "").trim()) {
    out.push(check("info", "no_description", `${label} has no description.`));
  }

  return out;
}

/**
 * Tier-band sanity. Volume ("Full") and graduated ("Partial") are genuinely
 * different maths, and the failure modes differ too — a volume table can price
 * a larger order LOWER in total than a smaller one, which is almost always
 * unintended and is invisible unless you compute it.
 */
function tierChecks(plan, tiers, currency) {
  const out = [];
  const label = plan.name;

  for (let i = 1; i < tiers.length; i += 1) {
    const prevEnd = num(tiers[i - 1].EndRange);
    const start = num(tiers[i].StartRange);
    if (prevEnd === null || start === null) continue;
    if (start > prevEnd + 1) {
      out.push(
        check(
          "block",
          "tier_gap",
          `${label}: nothing priced between ${prevEnd} and ${start} units.`,
        ),
      );
    } else if (start <= prevEnd) {
      out.push(
        check("block", "tier_overlap", `${label}: tiers overlap at ${start}–${prevEnd} units.`),
      );
    }
  }

  const last = tiers[tiers.length - 1];
  if (num(last.EndRange) !== null) {
    out.push(
      check(
        "review",
        "tier_capped",
        `${label}: the top tier ends at ${num(last.EndRange)} units — orders above that have no price.`,
      ),
    );
  }

  if (plan.unit_price_type === "Full") {
    for (let i = 1; i < tiers.length; i += 1) {
      const prev = tiers[i - 1];
      const cur = tiers[i];
      const boundary = num(prev.EndRange);
      if (boundary === null) continue;
      const before = boundary * (num(prev.Marketplace_price) ?? 0);
      const after = (boundary + 1) * (num(cur.Marketplace_price) ?? 0);
      if (after < before) {
        out.push(
          check(
            "review",
            "volume_non_monotonic",
            `${label}: at volume rates, ${boundary + 1} units (${money(after, currency)}) ` +
              `costs LESS than ${boundary} units (${money(before, currency)}).`,
          ),
        );
      }
    }
  }
  return out;
}

/**
 * Worked example — the fastest correctness check a reviewer can make, and the
 * only way to prove which maths is in force. Same tiers, same quantity, two
 * different totals.
 */
export function quoteAt(plan, qty) {
  const tiers = sortedTiers(plan);
  if (!tiers.length) return null;
  const rate = (t) => num(t.Marketplace_price) ?? 0;

  if (plan.unit_price_type === "Full") {
    const band =
      tiers.find((t) => {
        const s = num(t.StartRange) ?? 0;
        const e = num(t.EndRange);
        return qty >= s && (e === null || qty <= e);
      }) || tiers[tiers.length - 1];
    return { total: qty * rate(band), workings: `${qty} × ${rate(band)}` };
  }

  // Partial / graduated: each slice at its own rate.
  let remaining = qty;
  let total = 0;
  const parts = [];
  for (const t of tiers) {
    if (remaining <= 0) break;
    const s = num(t.StartRange) ?? 0;
    const e = num(t.EndRange);
    const span = e === null ? remaining : Math.max(0, e - s + 1);
    const take = Math.min(remaining, span);
    if (take <= 0) continue;
    total += take * rate(t);
    parts.push(`${take} × ${rate(t)}`);
    remaining -= take;
  }
  return { total, workings: parts.join(" + ") };
}

/* ── the build ───────────────────────────────────────────────────────── */

/**
 * Turn the raw payload into one section per plan type, each holding a
 * plan × period grid. Grouping by plan NAME is what recovers the matrix: the
 * backend stores one pricing row per (type, plan, period), so "Basic monthly"
 * and "Basic yearly" are two rows that the old screen showed on two different
 * screens. They are one product decision and belong on one line.
 */
export function buildReview({
  planType = [],
  intervals = [],
  plans = [],
  features = [],
  changeJson = {},
  planChanges = {},
  vendorId,
  ownerVendorId,
}) {
  const capIndex = indexCapFindings(changeJson);
  // Every unit id actually present in this payload. A finding pointing at one
  // of these joins exactly and must never also match by name.
  capIndex.resolvable = new Set(
    plans.flatMap((p) => (p.unit_ids || []).map((u) => String(u.id))),
  );
  const isHouseVendor = Number(vendorId) === ADMIN_VENDOR_ID;
  const isReseller =
    ownerVendorId !== undefined &&
    ownerVendorId !== null &&
    String(ownerVendorId) !== String(vendorId);

  const sections = planType.map((type, typeIndex) => {
    const periods = intervals
      .map((it, index) => ({ ...it, index }))
      .filter((it) => it.typeId === typeIndex)
      .map((it) => ({
        index: it.index,
        interval: it.interval,
        n_interval: it.n_interval,
        label: periodLabel(it),
        months: monthsIn(it),
      }));

    const mine = plans.filter((p) => p.typeId === typeIndex);

    // Preserve backend sort_order: first appearance wins.
    const names = [];
    mine.forEach((p) => {
      if (!names.includes(p.name)) names.push(p.name);
    });

    const rows = names.map((name) => {
      const cells = periods.map((period) => {
        const plan = mine.find((p) => p.name === name && p.interval === period.index);
        if (!plan) return { period, plan: null, missing: true };

        const unit = headlineUnit(plan);
        const margin = marginOf(unit);
        const currency = plan.currency || "INR";
        const capFinding = findCap(capIndex, plan, unit);
        const change = planChanges?.[plan.id];
        const unitDiffs = change?.unitChanges?.[unit?.id] || {};

        const priceDiff = unitDiffs.Marketplace_price || null;
        const vendorDiff = unitDiffs.Vendor_price || null;
        const priceDelta =
          priceDiff && num(priceDiff.before) ? ((num(priceDiff.after) - num(priceDiff.before)) / num(priceDiff.before)) * 100 : null;

        const checks = checksForPrice({
          plan,
          unit,
          margin,
          capFinding,
          isHouseVendor,
          currency,
        });

        return {
          period,
          plan,
          unit,
          tiers: sortedTiers(plan),
          currency,
          ...margin,
          perMonth:
            period.months && margin.marketplace !== null
              ? margin.marketplace / period.months
              : null,
          isNew: Boolean(change?.fieldChanges?.has?.("__new_plan")),
          changed: Boolean(change?.isChanged),
          fieldDiffs: change?.fieldDiffs || {},
          unitDiffs,
          priceDiff,
          vendorDiff,
          priceDelta,
          capFinding,
          checks,
          missing: false,
        };
      });

      // A plan priced for some periods but not others is nearly always an
      // omission rather than a decision, so it is called out rather than left
      // as an empty square the reviewer has to notice.
      const present = cells.filter((c) => !c.missing);
      const gapChecks =
        present.length > 0 && present.length < periods.length
          ? cells
              .filter((c) => c.missing)
              .map((c) =>
                check(
                  "review",
                  "missing_period",
                  `${name} has no ${c.period.label.toLowerCase()} price, but is priced for ${present
                    .map((p) => p.period.label.toLowerCase())
                    .join(" and ")}.`,
                ),
              )
          : [];

      return { name, cells, gapChecks };
    });

    // Annual should not cost more per month than monthly. If it does the
    // vendor has almost certainly transposed two numbers.
    const inversions = [];
    rows.forEach((row) => {
      const priced = row.cells.filter((c) => !c.missing && c.perMonth !== null && c.perMonth > 0);
      if (priced.length < 2) return;
      const shortest = priced.reduce((a, b) => (a.period.months <= b.period.months ? a : b));
      priced.forEach((c) => {
        if (c === shortest) return;
        if (c.perMonth > shortest.perMonth * 1.0001) {
          inversions.push(
            check(
              "review",
              "period_inversion",
              `${row.name}: ${c.period.label.toLowerCase()} works out at ` +
                `${money(c.perMonth, c.currency)}/month, MORE than ${shortest.period.label.toLowerCase()} ` +
                `at ${money(shortest.perMonth, shortest.currency)}/month.`,
            ),
          );
        }
      });
    });

    const currencies = Array.from(
      new Set(rows.flatMap((r) => r.cells.filter((c) => !c.missing).map((c) => c.currency))),
    );
    const currencyChecks =
      currencies.length > 1
        ? [
            check(
              "review",
              "mixed_currency",
              `${type.name} mixes ${currencies.join(" and ")} across its plans.`,
            ),
          ]
        : [];

    // Deduped: the same condition reached through two paths (a cap finding
    // attached to two rows, a currency clash counted per plan) is one thing
    // for the reviewer to decide, not two.
    const seenCheck = new Set();
    const allChecks = [
      ...rows.flatMap((r) => r.cells.filter((c) => !c.missing).flatMap((c) => c.checks)),
      ...rows.flatMap((r) => r.gapChecks),
      ...inversions,
      ...currencyChecks,
    ]
      .filter((c) => {
        const k = `${c.severity}|${c.code}|${c.text}`;
        if (seenCheck.has(k)) return false;
        seenCheck.add(k);
        return true;
      })
      .map((c) => ({ ...c, where: type.name }));

    return {
      typeIndex,
      name: type.name,
      template: type.template,
      periods,
      rows,
      features: features.filter((f) => f.typeId === typeIndex),
      plans: mine,
      checks: allChecks,
      currency: currencies[0] || "INR",
    };
  });

  const cells = sections.flatMap((s) => s.rows.flatMap((r) => r.cells.filter((c) => !c.missing)));
  const checks = sections.flatMap((s) => s.checks);

  const counts = {
    types: sections.length,
    periods: new Set(sections.flatMap((s) => s.periods.map((p) => `${s.typeIndex}:${p.index}`))).size,
    plans: sections.reduce((n, s) => n + s.rows.length, 0),
    prices: cells.length,
    changed: cells.filter((c) => c.changed).length,
    added: cells.filter((c) => c.isNew).length,
    overCap: cells.filter((c) => c.capFinding).length,
    blocking: checks.filter((c) => c.severity === "block").length,
    review: checks.filter((c) => c.severity === "review").length,
  };

  const verdict = counts.blocking > 0 ? "block" : counts.review > 0 ? "review" : "ok";

  // A submission with a before-side is a revision; one without is a first cut.
  const hasBaseline = Boolean(
    changeJson?.plans?.before?.length || Object.keys(planChanges || {}).length,
  );

  return { sections, counts, checks, verdict, isReseller, isHouseVendor, hasBaseline };
}
